import { useTranslation } from 'react-i18next';
import React, { ComponentType, MouseEvent, useCallback, useMemo } from 'react';
import {
  OutgoingMessageChatMessageKind,
  OutgoingMessageResponseStatus,
} from '@engyalo/salesdesk-chat-socket-ts-client';
import { useFeatureFlag, flag } from '../../../hooks/useFeatureFlag';
import { formatMessageTime } from '../../../helpers/formatterDates';
import { environmentUtils } from '../../../utils';
import { ErrorMessage, MessageRow, MessageWrapper } from './styled';
import TextMessage from './TextMessage';
import AudioMessage from './AudioMessage';
import ImageMessage from './ImageMessage';
import VideoMessage from './VideoMessage';
import FileMessage from './FileMessage';
import InteractiveMessage from './InteractiveMessage';
import { ChatMessageType, MessageType, SpecificMessageProps } from '../Message';
import LocationMessage from './LocationMessage';
import { getMessageStatus } from './utils';
import ContactInfoArea from './ContactInfoArea';
import { AuthorRoles } from './ChatMessage.constants';
import ReplyAction from './ReplyAction';
import QuotedMessageBubble from '../../QuotedMessageBubble';

export type ChatMessageComponentProps = Omit<ChatMessageType, 'kind'> & {
  quotedContent?: React.ReactNode;
};

export const MESSAGE_COMPONENTS: Record<OutgoingMessageChatMessageKind, ComponentType<ChatMessageComponentProps>> = {
  text: TextMessage,
  audio: AudioMessage,
  image: ImageMessage,
  video: VideoMessage,
  document: FileMessage,
  location: LocationMessage,
  interactive: InteractiveMessage,
};

const ChatMessage: React.FC<SpecificMessageProps> = (props) => {
  const { chat, onClick, allMessages, ...restProps } = props;
  const { t, i18n } = useTranslation();
  const isDeliveredStatusEnabled = useFeatureFlag(flag.DELIVERED_STATUS);
  const isReadStatusEnabled = useFeatureFlag(flag.READ_STATUS);

  const message: MessageType = useMemo(() => ({ ...restProps, kind: 'chat', chat } as MessageType), [restProps, chat]);

  const scrollToQuotedMessage = useCallback(() => {
    if (!chat?.contextId) return;
    const target = document.querySelector(`[data-message-id="${CSS.escape(chat.contextId)}"]`);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.classList.add('highlight');
    setTimeout(() => target.classList.remove('highlight'), 1500);
  }, [chat?.contextId]);

  if (!chat) {
    return null;
  }

  const { kind, ...rest } = chat;
  const { pending, error, delivered, read, authorRole, name: nameProp, username, timestamp, status } = chat;
  const name = nameProp || username;

  const hasDeliveredStatus = status === OutgoingMessageResponseStatus.ResponseStatusDelivered;
  const hasReadStatus = status === OutgoingMessageResponseStatus.ResponseStatusRead;
  const messageStatus = getMessageStatus({
    pending,
    delivered: isDeliveredStatusEnabled && (delivered || hasDeliveredStatus),
    read: isReadStatusEnabled && (read || hasReadStatus),
  });

  const isAgent = authorRole === AuthorRoles.AGENT;
  const isBot = authorRole === AuthorRoles.BOT;
  const kindsWithAdditionalProps: Array<OutgoingMessageChatMessageKind> = [
    OutgoingMessageChatMessageKind.Text,
    OutgoingMessageChatMessageKind.Image,
  ];
  const additionalProps = {
    displayName: isBot ? `${t('flow')}: ${environmentUtils.getEnvironmentInstance()}` : name,
    sentAt: formatMessageTime(Number(timestamp), i18n.language),
    messageStatus: isAgent ? messageStatus : undefined,
    variant: error ? 'error' : undefined,
  };

  const MessageComponent = MESSAGE_COMPONENTS[kind];

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (error) {
      event.preventDefault();
    }

    onClick?.();
  };

  const quotedMessage = chat.contextId ? allMessages?.find((m) => m.chat?.id === chat.contextId) : undefined;
  const quotedContent = chat.contextId ? (
    <QuotedMessageBubble
      contextId={chat.contextId}
      contextFrom={quotedMessage?.chat?.name ?? chat.contextFrom ?? ''}
      quotedMessage={quotedMessage}
      onClick={scrollToQuotedMessage}
    />
  ) : undefined;

  return (
    <MessageRow error={error} onClick={handleClick} data-message-id={chat.id}>
      <MessageWrapper authorRole={authorRole}>
        <ContactInfoArea name={name} authorRole={authorRole} />
        <MessageComponent
          {...rest}
          {...(kindsWithAdditionalProps.includes(kind) && additionalProps)}
          quotedContent={quotedContent}
        />
        <ReplyAction message={message} />
      </MessageWrapper>
      {error && <ErrorMessage>{t('messages.error')}</ErrorMessage>}
    </MessageRow>
  );
};

export default ChatMessage;
